import {Breadcrumb, IBreadcrumbItem} from '@fluentui/react';
import {DetailsField, DetailsSection} from 'components';
import {CommentForm} from 'components/CommentForm';
import {CommentLevel} from 'components/CommentList';
import {renderParagraph} from 'components/EntityList/ColumnRenderers';
import {ParagraphDocumentsCollectionName} from 'components/Faq/EntityTypeMapper';
import {useQuestionCountsByEntityTypeQuery} from 'components/Faq/Faq.generated';
import {FaqPanel, FaqPanelMode} from 'components/Faq/FaqPanel';
import {FaqTooltipTranslationProvider} from 'components/Faq/FaqTooltipTranslationProvider';
import useQuestionsPermissions from 'components/Faq/hooks/useQuestionsPermissions';
import {FAQForm} from 'components/FAQForm';
import {FAQLevel} from 'components/FAQList';
import {LoadWrapper} from 'components/LoadWrapper';
import {useGetParagraphDetailsQuery} from 'features/RegulatoryDocuments/hooks/useGetParagraphDetails.generated';
import {useCommand, useFavoriteCommand} from 'hooks';
import React from 'react';
import {TFunction, useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {RegulatoryDocument, RegulatoryDocumentParagraph} from 'types';
import {useUserContext} from 'authentication/UserContext';
import {useParagraphDetailsFields} from './hooks/useParagraphDetailsFields';

export const initFieldRowsFromParagraphDetails = (
	paragraphDetails: RegulatoryDocumentParagraph,
	showAudit: boolean,
	t: TFunction,
	tEnum: TFunction,
	shouldShowFaq: boolean,
	regulatoryDocument?: RegulatoryDocument,
	questionCount?: number,
): DetailsField[][] => {
	const elements = renderParagraph(paragraphDetails, t, false);
	const status =
		paragraphDetails?.parent?.workflow?.status ??
		regulatoryDocument?.workflow?.status;
	return [
		[
			{
				data: elements,
				type: 'text',
				label: t('ElementsTextLabel'),
				key: 'elements',
			},
		],
		[
			{
				data: paragraphDetails ? `${paragraphDetails.page}` : '',
				type: 'text',
				label: t('PageLabel'),
				key: 'page',
			},
			{
				data: tEnum(`WorkflowStatus.${status}`),
				type: 'text',
				label: t('WorkflowStatus'),
				key: 'workflowStatus',
			},
		],
		shouldShowFaq
			? [
					{
						data: paragraphDetails?.summary ?? '',
						type: 'richtext',
						label: t('SummaryLabel'),
						audit: showAudit,
						auditFieldName: 'summary',
						key: 'summary',
					},
					{
						data: {
							count: questionCount,
							faqState: {
								mode: FaqPanelMode.ParagraphDetails,
								entity: paragraphDetails,
								parentId: paragraphDetails?.parent?.id,
								showPanel: true,
							},
							text: t('FaqText'),
						},
						type: 'faq',
						label: t('FaqLabel'),
						key: 'faqs',
					},
			  ]
			: [
					{
						data: paragraphDetails?.summary ?? '',
						type: 'richtext',
						label: t('SummaryLabel'),
						audit: showAudit,
						auditFieldName: 'summary',
						key: 'summary',
					},
			  ],
		[
			{
				data: paragraphDetails?.modelYear,
				type: 'text',
				label: t('ModelYearLabel'),
				audit: showAudit,
				auditFieldName: 'modelYear',
				key: 'modelYear',
			},
			{
				data: paragraphDetails?.comprehensive,
				type: 'date',
				label: t('ComprehensiveLabel'),
				audit: showAudit,
				auditFieldName: 'comprehensive',
				key: 'comprehensive',
			},
			{
				data: paragraphDetails?.dateNewTypes,
				type: 'date',
				label: t('DateNewTypesLabel'),
				audit: showAudit,
				auditFieldName: 'dateNewTypes',
				key: 'dateNewTypes',
			},
			{
				data: paragraphDetails?.dateNewRegistration,
				type: 'date',
				label: t('DateNewRegistrationLabel'),
				audit: showAudit,
				auditFieldName: 'dateNewRegistration',
				key: 'dateNewRegistration',
			},
		],
		[
			{
				data: paragraphDetails?.keywords,
				type: 'array',
				label: t('KeywordsLabel'),
				audit: showAudit,
				auditFieldName: 'keywordRefs',
				key: 'keywords',
			},
			{
				data: paragraphDetails?.driveVariants,
				type: 'array',
				label: t('DriveVariantsLabel'),
				audit: showAudit,
				auditFieldName: 'driveVariantRefs',
				key: 'driveVariants',
			},
		],
		[
			{
				data: paragraphDetails?.vehicleCategories,
				type: 'array',
				label: t('VehicleCategoriesLabel'),
				audit: showAudit,
				auditFieldName: 'vehicleCategoryRefs',
				key: 'vehicleCategories',
			},
			{
				data: paragraphDetails?.categories,
				type: 'array',
				label: t('CategoriesLabel'),
				audit: showAudit,
				auditFieldName: 'categoryRefs',
				key: 'categories',
			},
		],
		[
			{
				data: paragraphDetails?.requirements,
				type: 'array',
				label: t('RequirementsLabel'),
				audit: showAudit,
				auditFieldName: 'requirementRefs',
				key: 'requirements',
			},
			{
				data: paragraphDetails?.tags,
				type: 'array',
				label: t('TagsLabel'),
				audit: showAudit,
				auditFieldName: 'tagRefs',
				key: 'tags',
			},
		],
		[
			{
				data: paragraphDetails?.phaseIn,
				type: 'phase',
				label: t('PhaseInLabel'),
				audit: showAudit,
				auditFieldName: 'phaseIn',
				key: 'phaseIn',
			},
		],
		[
			{
				data: paragraphDetails?.phaseOut,
				type: 'phase',
				label: t('PhaseOutLabel'),
				audit: showAudit,
				auditFieldName: 'phaseOut',
				key: 'phaseOut',
			},
		],
		[
			{
				data: paragraphDetails?.createdBy,
				type: 'personas',
				label: t('CreatedByLabel'),
				key: 'createdBy',
			},
			{
				data: paragraphDetails?.createdAt,
				type: 'date',
				label: t('CreatedAtLabel'),
				key: 'createdAt',
			},
			{
				data: paragraphDetails?.modifiedBy,
				type: 'personas',
				label: t('ModifiedByLabel'),
				key: 'modifiedBy',
			},
			{
				data: paragraphDetails?.modifiedAt,
				type: 'date',
				label: t('ModifiedAtLabel'),
				key: 'modifiedAt',
			},
		],
		[
			{
				data: paragraphDetails?.attachments,
				type: 'attachments',
				label: t('AttachmentsLabel'),
				key: 'attachments',
			},
		],
	];
};

const ParagraphDetailsPage: React.FC = () => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'ParagraphDetailsPage',
	});
	const {t: tEnum} = useTranslation('common/enums');
	const [showAudit, setShowAudit] = React.useState(false);
	const {regulationId, regulatoryDocumentId, paragraphId} = useParams();

	const {loading, data} = useGetParagraphDetailsQuery({
		variables: {
			regulatoryDocumentId: regulatoryDocumentId || '',
			paragraphId: paragraphId || '',
		},
	});

	const {data: questionSummaryData} = useQuestionCountsByEntityTypeQuery({
		variables: {
			entityType: ParagraphDocumentsCollectionName,
			parentId: regulatoryDocumentId,
		},
	});

	const paragraphDetails = React.useMemo(
		() =>
			(data?.regulatoryDocumentParagraph ?? {}) as RegulatoryDocumentParagraph,
		[data],
	);

	const {isAvk, isBav} = useUserContext();
	const isAvkOrBav = isAvk || isBav;

	const auditlogs = React.useMemo(() => {
		const decodedRegDocId = regulatoryDocumentId
			? atob(regulatoryDocumentId).substring('RegulatoryDocument\nd'.length)
			: undefined;
		return paragraphDetails?.auditLog?.filter(
			log => decodedRegDocId === undefined || log.entityId === decodedRegDocId,
		);
	}, [paragraphDetails, regulatoryDocumentId]);

	const comments = React.useMemo(
		() => paragraphDetails?.comments,
		[paragraphDetails],
	);

	const faqs = React.useMemo(() => paragraphDetails?.faqs, [paragraphDetails]);

	const itemsWithHref: IBreadcrumbItem[] = [
		{
			text: t('Regulations'),
			key: 'regulations',
			href: '/regulations',
		},
		{
			text: `${paragraphDetails?.parent?.regulation?.regulationNumber}`,
			key: 'regulatoryDocument',
			href: `/regulations/${regulationId}`,
		},
		{
			text: `${paragraphDetails?.parent?.name}`,
			key: `paragraphs`,
			href: `/regulations/${regulationId}/${regulatoryDocumentId}/paragraphs`,
		},
		{
			text: `${paragraphDetails?.enumeration}`,
			key: `paragraph`,
			href: `/regulations/${regulationId}/${regulatoryDocumentId}/paragraphs/${paragraphId}`,
		},
	];

	const {shouldShowFaq} = useQuestionsPermissions();
	const initialFieldRows: DetailsField[][] = React.useMemo(() => {
		const questionCount = questionSummaryData?.questionCountsByEntityType?.find(
			t => t.entityId === paragraphId,
		)?.count;
		return initFieldRowsFromParagraphDetails(
			paragraphDetails,
			showAudit,
			t,
			tEnum,
			shouldShowFaq,
			undefined,
			questionCount,
		);
	}, [paragraphDetails, showAudit, tEnum, t, questionSummaryData]);

	const filteredFieldRows = useParagraphDetailsFields({
		rows: initialFieldRows,
		isAvkOrBav,
	});

	useCommand(
		{
			key: 'toggleAudit',
			iconProps: {
				iconName: 'History',
			},
			farCommand: true,
			priority: 4,
			onClick() {
				setShowAudit(!showAudit);
			},
			title: t('Audit'),
		},
		[showAudit],
	);

	useFavoriteCommand(paragraphId || '', []);

	return (
		<LoadWrapper loading={loading}>
			<Breadcrumb items={itemsWithHref} />
			<DetailsSection
				title={paragraphDetails?.enumeration || ''}
				initialFieldRows={filteredFieldRows}
				auditLogData={auditlogs}
			/>
			<CommentForm comments={comments || []} level={CommentLevel.Paragraph} />
			<FaqTooltipTranslationProvider>
				<FaqPanel parentId={paragraphId} />
			</FaqTooltipTranslationProvider>
			<FAQForm faqs={faqs || []} level={FAQLevel.Paragraph} />
		</LoadWrapper>
	);
};

export default ParagraphDetailsPage;
